import { motion } from "framer-motion";
import { Star, Info, Goal, Handshake } from "lucide-react";
import { useLang } from "@/i18n";

const ABOUT_IMG = "/about-service.webp";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const commitments = [
  { title: "c1t", text: "c1x" },
  { title: "c2t", text: "c2x" },
  { title: "c3t", text: "c3x" },
  { title: "c4t", text: "c4x" },
  { title: "c5t", text: "c5x" },
  { title: "c6t", text: "c6x" },
];

export default function About() {
  const { t } = useLang();

  return (
    <section id="about" className="py-28 lg:py-40 bg-navy-800/40 border-y border-brand/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...reveal} className="relative order-2 lg:order-1">
            <div className="absolute -inset-4 border border-brand/25 -translate-x-5 translate-y-5" aria-hidden="true" />
            <div className="relative overflow-hidden" data-testid="about-image-frame">
              <img
                src={ABOUT_IMG}
                alt="Client support specialist with headset at a modern office desk, world map on the wall"
                className="w-full h-[26rem] lg:h-[32rem] object-cover"
                data-testid="about-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent" />
            </div>
          </motion.div>

          <motion.div {...reveal} className="order-1 lg:order-2">
            <p className="overline-label" data-testid="about-overline">{t("a_over")}</p>
            <h2 className="mt-6 font-serif font-semibold text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="about-heading">
              {t("a_h1a")} <span className="text-brand-300">{t("a_h1b")}</span>
            </h2>
            <div className="mt-8 space-y-5 text-base text-cream/60 leading-relaxed" data-testid="about-copy">
              <p>{t("a_p1")}</p>
              <p>{t("a_p2")}</p>
              <p>{t("a_p3")}</p>
            </div>
            <p className="mt-8 text-xs text-cream/40 leading-relaxed border-l-2 border-brand/40 pl-5" data-testid="about-availability-note">
              {t("a_note")}
            </p>
            <p className="mt-6 inline-flex items-center gap-3 border border-brand/25 bg-navy-900 px-5 py-4 text-xs sm:text-sm text-cream/70" data-testid="about-partner-line">
              <Handshake size={16} className="text-brand flex-none" />
              {t("partner_line")}
            </p>
          </motion.div>
        </div>

        <div className="mt-32">
          <motion.div {...reveal} className="max-w-2xl">
            <p className="overline-label" data-testid="commitment-overline">{t("c_over")}</p>
            <h3 className="mt-6 font-serif font-semibold text-3xl sm:text-4xl" data-testid="commitment-heading">
              {t("c_h1a")} <span className="text-brand-300">{t("c_h1b")}</span>
            </h3>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commitments.map(({ title, text }, i) => (
              <motion.div
                key={title}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="group bg-navy-800 border border-brand/10 p-8 hover:border-brand/30 hover:-translate-y-1 transition-all duration-500"
                data-testid={`commitment-card-${i}`}
              >
                <Star size={18} className="text-brand" fill="currentColor" />
                <h4 className="mt-5 font-serif font-semibold text-xl text-cream">{t(title)}</h4>
                <p className="mt-3 text-sm text-cream/55 leading-relaxed">{t(text)}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-24 grid lg:grid-cols-2 gap-6">
          <motion.div
            {...reveal}
            className="border border-brand/30 bg-navy-900 p-10"
            data-testid="independent-provider-panel"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 border border-brand/40 text-brand mb-6">
              <Info size={20} />
            </span>
            <h3 className="font-serif font-semibold text-2xl sm:text-3xl text-cream">{t("indep_t")}</h3>
            <div className="mt-5 space-y-4 text-sm text-cream/55 leading-relaxed">
              <p>{t("indep_p1")}</p>
              <p>{t("indep_p2")}</p>
            </div>
          </motion.div>

          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.12 }}
            className="bg-brand p-10 flex flex-col justify-center"
            data-testid="our-goal-panel"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 border border-white/40 text-white mb-6">
              <Goal size={20} />
            </span>
            <h3 className="font-serif font-semibold text-2xl sm:text-3xl text-white">{t("goal_t")}</h3>
            <p className="mt-5 font-serif text-xl sm:text-2xl text-white/85 leading-snug">
              "{t("goal_q")}"
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
